import { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Grid, Input } from 'antd'
import clsx from 'clsx'
import type { ChangeEvent } from 'react'

import B2becButton from '@/components/B2becButton'
import B2becInfoLayout from '@/components/B2becInfoLayout/B2becInfoLayout'
import B2becPagination from '@/components/B2becPagination/B2becPagination'
import { PlusIcon, SearchIcon } from '@/components/Icons'
import CreateWishlistModal from '@/components/Wishlist/CreateWishlistModal'
import { useAppSelector } from '@/store/hook'
import { selectUserId } from '@/store/slices/authenticationSlice'
import { useDocumentTitle } from '@/hooks'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { MODULE_WISHLIST_RIGHTS } from '@/libs/constants/modulerights'
import { linkGenerator } from '@/libs/utils/language'

import { useDeleteWishlist, useFetchWishlists, useRenameWishlist, WISHLIST_OVERVIEW_PAGE_SIZE } from './useFetchWishlist'
import WishlistList from './WishlistList'
import type { Wishlist } from './types'
import styles from './WishlistOverview.module.scss'

interface WishlistOverviewProps {
  breadcrumbs: {
    title: string
    path: string
  }[]
}

const WishlistOverview = ({ breadcrumbs }: WishlistOverviewProps) => {
  useDocumentTitle('pageTitle.wishlistOverview')
  useBreadcrumbs(breadcrumbs)
  const { t } = useTranslation()
  const ownerId = useAppSelector(selectUserId)
  const navigate = useNavigate()

  useAdobeAnalysis(null, {
    pageName: 'wishlist overview page',
    pageId: '46',
    pageType: PAGE_TYPES.WISHLIST,
  })

  const {
    fetchOwnWishlists,
    fetchSharedWishlists,
    ownPageNumber,
    sharedPageNumber,
    handleChangeOwnPage,
    handleChangeSharedPage,
    handleChangeText,
    searchQuery,
  } = useFetchWishlists()

  const { mutate: deleteWishlistMutation, isPending: isDeletingWishlist } = useDeleteWishlist()

  const { mutate: renameWishlistMutation, isPending: isRenamingWishlist } = useRenameWishlist()

  const { lg } = Grid.useBreakpoint()

  const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false)

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChangeText(event.target.value)
  }

  const handleCreateModalOpen = () => {
    setIsCreateModalVisible(true)
  }

  const handleCreateModalClose = () => {
    setIsCreateModalVisible(false)
  }

  const mapWishlistItems = useMemo(
    () => (items: Wishlist[] | undefined) =>
      (items ?? []).map((item) => ({
        ...item,
        isShared: item.ownerId !== ownerId,
        onCardClick: () => {
          navigate(linkGenerator(`/wishlist/${item.wishlistId}/owner/${item.ownerId}`))
        },
        onDelete: () => {
          deleteWishlistMutation({
            wishlistOwnerId: ownerId ?? '',
            wishlistId: item.wishlistId,
          })
        },
        onRename: (newName: string) => {
          renameWishlistMutation({
            wishlistId: item.wishlistId,
            ownerId: ownerId ?? '',
            newName,
          })
        },
      })),
    [deleteWishlistMutation, renameWishlistMutation, navigate, ownerId],
  )

  const ownWishlistItems = useMemo(
    () => mapWishlistItems(fetchOwnWishlists.data?.items),
    [mapWishlistItems, fetchOwnWishlists.data?.items],
  )

  const sharedWishlistItems = useMemo(
    () => mapWishlistItems(fetchSharedWishlists.data?.items),
    [mapWishlistItems, fetchSharedWishlists.data?.items],
  )

  const isActionsDisabled = isDeletingWishlist || isRenamingWishlist

  return (
    <B2becInfoLayout
      className={clsx(styles.wishlistOverview, lg && styles.desktop)}
    >
      <B2becInfoLayout.Title>
        <div className={styles.title}>
          <h1>{t('pageTitle.wishlistOverview')}</h1>
          <PermissionWrapper permission={MODULE_WISHLIST_RIGHTS.ADD_WISHLIST}>
            <div className={styles.titleActions}>
              {lg
                ? (
                  <B2becButton
                    disabled={isActionsDisabled}
                    onClick={handleCreateModalOpen}
                  >
                    {t('wishlist.actions.addWishlist')}
                  </B2becButton>
                )
                : (
                  <button
                    className={styles.addButton}
                    disabled={isActionsDisabled}
                    type="button"
                    onClick={handleCreateModalOpen}
                  >
                    <PlusIcon />
                  </button>
                )}
            </div>
          </PermissionWrapper>
        </div>
      </B2becInfoLayout.Title>
      <B2becInfoLayout.Content>
        <div className={styles.searchInputContainer}>
          <Input
            disabled={isActionsDisabled}
            placeholder={t('buttonTexts.search')}
            prefix={<SearchIcon className="user-management__search-icon" />}
            onChange={handleSearchChange}
          />
        </div>

        <div className={styles.wishlistZone}>
          <h2 className={styles.zoneTitle}>{t('wishlist.zone.myWishlists')}</h2>
          <WishlistList
            isLoading={fetchOwnWishlists.isFetching}
            items={ownWishlistItems}
            pageNumber={ownPageNumber}
            pageSize={WISHLIST_OVERVIEW_PAGE_SIZE}
            disableDragAndDrop={Boolean(searchQuery)}
          />
          {(fetchOwnWishlists.data?.totalCount ?? 0) > WISHLIST_OVERVIEW_PAGE_SIZE && (
            <B2becPagination
              current={ownPageNumber}
              disabled={fetchOwnWishlists.isFetching}
              pageSize={WISHLIST_OVERVIEW_PAGE_SIZE}
              total={fetchOwnWishlists.data?.totalCount}
              onPageChange={handleChangeOwnPage}
            />
          )}
        </div>

        <div className={styles.wishlistZone}>
          <h2 className={styles.zoneTitle}>{t('wishlist.zone.sharedWithMe')}</h2>
          <WishlistList
            isLoading={fetchSharedWishlists.isFetching}
            items={sharedWishlistItems}
            pageNumber={sharedPageNumber}
            pageSize={WISHLIST_OVERVIEW_PAGE_SIZE}
            disableDragAndDrop
          />
          {(fetchSharedWishlists.data?.totalCount ?? 0) > WISHLIST_OVERVIEW_PAGE_SIZE && (
            <B2becPagination
              current={sharedPageNumber}
              disabled={fetchSharedWishlists.isFetching}
              pageSize={WISHLIST_OVERVIEW_PAGE_SIZE}
              total={fetchSharedWishlists.data?.totalCount}
              onPageChange={handleChangeSharedPage}
            />
          )}
        </div>

        {isCreateModalVisible && (
          <PermissionWrapper permission={MODULE_WISHLIST_RIGHTS.ADD_WISHLIST}>
            <CreateWishlistModal
              open={isCreateModalVisible}
              onClose={handleCreateModalClose}
            />
          </PermissionWrapper>
        )}
      </B2becInfoLayout.Content>
    </B2becInfoLayout>
  )
}

export default memo(WishlistOverview)
